import React, { useCallback, useRef, useState } from 'react';
import AliasEditor from './AliasEditor';
import styled from 'styled-components';
import { IContextualMenuItem } from '@fluentui/react';
import { IconWrapper, SvgIcon } from '../Icon';
import { IAliasOption } from './types';

interface IProps {
  disabled?: boolean;
  aliases: IAliasOption[];
  inline: boolean;
  extraMenuItems?: IContextualMenuItem[];
  setIsMenuOpen?: (isMenuOpen: boolean) => void;
  // we haven't update all dropdown menus, to keep the consistency of item icons,
  // use this prop to control the display of the menu item icon.
  showRenameIcon?: boolean;
}

const AliasEditorContainer = styled.div<{ $inline: boolean }>`
  position: relative;
  display: ${({ $inline }) => ($inline ? 'inline-flex' : 'flex')};
  align-items: center;
`;

const ChildrenContainer = styled.div`
  flex-grow: 1;
  min-width: 0; // grow but fit in the constrains of the parent element
`;

/**
 * This is a wrapper component which enhances the children components with Alias Editor functionality
 * Shows "three dots" menu next to the children after and hides it after a small delay
 *
 * The component is rendered either as block (inline=false) or inline (inline=true) element
 *
 * It shows / hide the {@link AliasEditor} depending on the disabled property
 */
const AliasEditorWrapper = ({
  aliases,
  disabled,
  inline,
  extraMenuItems,
  children,
  setIsMenuOpen,
  showRenameIcon = false,
}: React.PropsWithChildren<IProps>) => {
  // anchor to the Alias Editor to this element
  const menuAnchor = useRef<HTMLDivElement | null>(null);
  const [isMenuVisible, setIsMenuVisible] = useState(false);

  const hideMenu = () => {
    setIsMenuVisible(false);
  };

  const toggleMenuAndStopPropagation = useCallback(
    (event: React.MouseEvent) => {
      if (isMenuVisible) {
        hideMenu();
      } else {
        setIsMenuVisible(true);
      }
      event.stopPropagation();
    },
    [isMenuVisible],
  );

  return (
    <AliasEditorContainer $inline={inline}>
      <ChildrenContainer>{children}</ChildrenContainer>
      <IconWrapper size={16} ref={menuAnchor}>
        {
          <SvgIcon
            name="dots-three-outline-vertical"
            width={16}
            height={16}
            onClick={toggleMenuAndStopPropagation}
            isActive={isMenuVisible}
            isHoverable={true}
            isDisabled={disabled}
          />
        }
      </IconWrapper>

      <AliasEditor
        isMenuVisible={isMenuVisible}
        menuAnchor={menuAnchor}
        aliases={aliases}
        extraMenuItems={extraMenuItems}
        onDismiss={hideMenu}
        setMenuIsOpen={setIsMenuOpen}
        showRenameIcon={showRenameIcon}
      />
    </AliasEditorContainer>
  );
};

export default AliasEditorWrapper;
